import { createContext, FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import classNames from 'classnames';

import { ProgramPoints } from '@rikstv/play-player/src/components/programPoints/ProgramPoints';
import { Spinner } from '@rikstv/play-player/src/components/Spinner/Spinner';
import { Gutter, H1 } from '@rikstv/shared-components';

import { useIsAuthenticated } from '../../hooks/useIsAuthenticated';
import { commonRoutes } from '../../router/router.path';
import { addParameters } from '../../utils/url/url.utils';
import { AssetNotFound } from '../errorPage/AssetNotFound';
import { useTvGuideChannels } from '../tvGuide-v2/useTvGuideChannels';

import { PreviouslyAiredGrid } from './archive/PreviouslyAiredGrid';
import { ChannelGrid } from './channelOverview/ChannelGrid';
import { ExternalChannelPoster } from './live/ExternalChannelPoster';
import { useLiveChannel } from './live/useLiveChannel';
import { PlayerDialogues } from './coverpage.types';
import { CoverPageHero } from './CoverPageHero';
import { PlaybackLive } from './PlaybackLive';

import styles from './CoverPageChannel.module.css';

interface Props extends PlayerDialogues {
  channelId?: string;
}

export const ChannelContext = createContext<string | undefined>(undefined);

export const CoverPageChannel: FC<Props> = ({ channelId, noAccessDialogue, signupDialogue }) => {
  const { defaultChannelId, isLoading: isDefaultChannelLoading } = useGetDefaultChannelId();
  const resolvedChannelId = channelId ?? defaultChannelId ?? '';

  const isValidChannelId = /^\d+$/.test(resolvedChannelId);

  const { channel, isLoading, currentProgram } = useLiveChannel(isValidChannelId ? resolvedChannelId : undefined);

  if (!isValidChannelId) {
    // might have ended up here with channel name instead of id, so attempt to resolve and redirect
    return <ChannelNameRedirector channelName={resolvedChannelId} />;
  }

  if ((isLoading || isDefaultChannelLoading) && !channel) {
    return <Spinner />;
  }

  if (!channel) {
    return <AssetNotFound contextInfo={`CoverPageChannel channelId=${resolvedChannelId}`} />;
  }

  setLastPlayedChannel(resolvedChannelId);

  const image = `${currentProgram?.image || channel.posterUrl}?width=800`;
  const titlePrefix = channel.isExternal ? 'Direktesending på ' : '';
  const title = `${titlePrefix}${channel.name}`;
  const playerComponent = channel.isExternal ? (
    <ExternalChannelPoster channel={channel} poster={image} />
  ) : (
    <PlaybackLive
      noAccessDialogue={noAccessDialogue}
      signupDialogue={signupDialogue}
      channel={channel}
      posterUrl={image}
    />
  );

  return (
    <ChannelContext.Provider value={resolvedChannelId}>
      <div itemScope itemType={'http://schema.org/BroadcastService'} className={classNames(styles.coverpage)}>
        <Helmet>
          <title>{channel.name}</title>
          <meta property="og:image" content={image ? addParameters(image, { width: '1200' }) : ''} />
          <link rel="canonical" href={commonRoutes.watchLive(channel.id, channel.name)} />
        </Helmet>
        <H1 className="sr-only">{title}</H1>
        <CoverPageHero
          programPointsComponent={<ProgramPoints channel={channel} />}
          image={image}
          playerComponent={playerComponent}
        />
        <section className={styles.next}>
          <ChannelGrid />
        </section>
        <Gutter>
          <section className={styles.archive}>
            <PreviouslyAiredGrid name={channel.name} archiveUrl={channel.archiveUrl} />
          </section>
        </Gutter>
      </div>
    </ChannelContext.Provider>
  );
};

const ChannelNameRedirector: FC<{ channelName: string }> = ({ channelName }) => {
  const { channels, isLoading } = useTvGuideChannels({ includePrograms: false });

  if (isLoading) {
    return <Spinner />;
  }

  const channel = channels?.find(c => c.serviceName.toLowerCase() === channelName);
  if (!channel) {
    return <AssetNotFound contextInfo={`ChannelNameRedirector, channelName=${channelName}}`} />;
  }

  return <Navigate to={commonRoutes.watchLive(channel.channelId, channel.serviceName)} replace={true} />;
};

const useGetDefaultChannelId = () => {
  const isLoggedIn = useIsAuthenticated();
  const { channels, isLoading } = useTvGuideChannels({ includePrograms: false });
  const lastPlayedChannel = getLastPlayedChannel();

  const defaultChannelId = isLoggedIn
    ? channels?.filter(channel => channel.subscription && !channel.isExternal)[0]?.channelId?.toString()
    : channels?.[0]?.channelId?.toString();
  return { defaultChannelId: lastPlayedChannel ?? defaultChannelId, isLoading };
};

const LAST_PLAYED_CHANNEL_KEY = 'lastPlayedChannel';

function getLastPlayedChannel() {
  return localStorage.getItem(LAST_PLAYED_CHANNEL_KEY);
}

function setLastPlayedChannel(channelId: string) {
  localStorage.setItem(LAST_PLAYED_CHANNEL_KEY, channelId);
}
